import { Box, Checkbox, Flex, For, HStack, Input, InputAddon, Spinner, Stack, Switch, Table, TableHeader, Text, useBreakpointValue, useDisclosure } from "@chakra-ui/react"
import { useWebHelper } from "@viabl/echo-web-client-core";
import { useEffect, useState } from "react";
import { AiFillCaretLeft, AiFillCaretRight } from "react-icons/ai";
import { BiSearch } from "react-icons/bi";
import { FaCheckCircle, FaMinus, FaPlus } from "react-icons/fa";
import { IoMdRefresh } from "react-icons/io";
import { useColorMode } from "../ui/color-mode";
import { toaster } from "../ui/toaster";
import { Button } from "../ui/button";

interface IProps {
    cb: Function,
    userId: string,
}

interface IRole {
    id: string
    name: string
    is_assigned_to_user: boolean
}

export default function EchoBundlesEditor(props: IProps) {
    const { onOpen, onClose } = useDisclosure();
    const [id, setId] = useState<string>(props.userId)
    const [rows, setRows] = useState<any>([])
    const [page, setPage] = useState<number>(0)
    const [search, setSearch] = useState<string>("")
    const [debouncedSearch, setDebouncedSearch] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false);
    const [nextPageNo, setNextPageNo] = useState<number>(0);
    const [maxPages, setMaxPages] = useState<number>(0);
    const [active, setActive] = useState(false)
    const [pendingRoleIds, setPendingRoleIds] = useState<Record<string, boolean>>({});
    const { colorMode, toggleColorMode } = useColorMode();

    const webHelpers = useWebHelper();

    const pageSize = useBreakpointValue({
        base: 5,
        md: 6,
        xl: 11,
    }, { fallback: "base" }) ?? 5;

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        setPage(0);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);

        return () => clearTimeout(timeout);
    }, [search]);

    function updateTable(query: string = search) {
        setLoading(true)
        setNextPageNo(page);
        webHelpers.GetAsync("/api/rolebundles/search?page=" + page + "&pageSize=" + pageSize + "&query=" + query + "&userId=" + props.userId + "&showAll=" + !active, "auth").then((data: any) => {
            if (data === undefined) {
                toaster.create({
                    title: `${data}: Unable to fetch bundles from the API`,
                    type: "error",
                });
            } else {
                setRows(data.results);
                setMaxPages(data.max_pages);
                setLoading(false);
            }
        }
        ).catch((error) => {
            setLoading(false);
            toaster.create({
                title: `${error.status}: Unable to fetch bundles from the API`,
                type: "error",
            });
        });
    }

    const onForwardClick = () => {
        setPage(page + 1)
    }

    const onBackClick = () => {
        setPage(page - 1)
    }

    const onRefreshClick = () => {
        setPage(0)
        updateTable();
    }

    useEffect(() => {
        setId(props.userId)
        updateTable(debouncedSearch)
    }, [page, pageSize, debouncedSearch, active, props.userId])

    const updateRowState = (roleId: string, isInBundle: boolean) => {
        setRows((currentRows: IRole[]) => {
            if (active && !isInBundle) {
                return currentRows.filter((role: IRole) => role.id !== roleId);
            }

            return currentRows.map((role: IRole) =>
                role.id === roleId
                    ? { ...role, is_assigned_to_user: isInBundle }
                    : role
            );
        });
    };

    const isRoleActive = (role: IRole) => active || role.is_assigned_to_user;

    const handleAdd = async (roleId: string) => {
        setPendingRoleIds((currentPending) => ({ ...currentPending, [roleId]: true }));

        try {
            await webHelpers.PutAsync("/api/user/" + id + "/rolebundle/" + roleId, "auth");
            updateRowState(roleId, true);

            toaster.create({
                title: `Bundle added to user`,
                type: "success",
            });
        } catch (error: any) {
            toaster.create({
                title: `${error?.status ?? "Request failed"}: Unable to add bundle`,
                type: "error",
            });
        } finally {
            setPendingRoleIds((currentPending) => {
                const nextPending = { ...currentPending };
                delete nextPending[roleId];
                return nextPending;
            });
        }
    }

    const handleRemove = async (roleId: string) => {
        setPendingRoleIds((currentPending) => ({ ...currentPending, [roleId]: true }));

        try {
            await webHelpers.DeleteNoResultAsync("/api/user/" + id + "/rolebundle/" + roleId, "auth");
            updateRowState(roleId, false);

            toaster.create({
                title: `Bundle removed from user`,
                type: "success",
            });
        } catch (error: any) {
            toaster.create({
                title: `${error?.status ?? "Request failed"}: Unable to remove bundle`,
                type: "error",
            });
        } finally {
            setPendingRoleIds((currentPending) => {
                const nextPending = { ...currentPending };
                delete nextPending[roleId];
                return nextPending;
            });
        }
    }

    const handleToggleRole = (roleId: string, isInBundle: boolean) => {
        if (isInBundle) {
            handleRemove(roleId);
        } else {
            handleAdd(roleId);
        }
    }

    return (
        <>
            <Stack
                direction={{ base: "column", lg: "row" }}
                width={"80%"}
                m={"auto"}
                pt={4}
                pb={8}
                px={20}
                align="center"
                justify={{ base: "", lg: "space-between" }}
            >
                <Flex marginLeft={"10px"} align={"center"} direction={"row"} >
                    <InputAddon
                        justifyContent={"center"}
                        height={"30px"}
                        marginTop={0}
                        borderColor={"backgroundLightGrey"}
                        background={"backgroundLightGrey"}
                    >
                        <BiSearch size={"18px"} />
                    </InputAddon>
                    <Input
                        bg={"backgroundDarkGrey"}
                        height={"30px"}
                        placeholder={" Search Bar"}
                        paddingLeft={"10px"}
                        value={search}
                        onChange={(e) => handleSearchChange(e)}
                        width={"200px"}
                    />
                </Flex>
                <HStack>
                    <Checkbox.Root
                        checked={active}
                        onCheckedChange={(e) => setActive(!!e.checked)}
                    >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Active</Checkbox.Label>
                    </Checkbox.Root>
                    <Button
                        size="sm"
                        onClick={onRefreshClick}
                        alignSelf={"right"}
                    >
                        <IoMdRefresh />
                    </Button>
                    <Button
                        disabled={(page === 0) ? true : false}
                        size="sm"
                        onClick={onBackClick}
                        alignSelf={"right"}
                    >
                        <AiFillCaretLeft />
                    </Button>
                    <Button
                        disabled={(page >= maxPages - 1) ? true : false}
                        size="sm"
                        onClick={onForwardClick}
                        alignSelf={"right"}
                    >
                        <AiFillCaretRight />
                    </Button>
                </HStack>
            </Stack>
            <Table.Root
                variant={"outline"}
                textAlign="center"
                m={"auto"}
                maxW={"80%"}
            >
                {loading ?
                    <Flex
                        direction={"row"}
                        justifyContent={"center"}
                        width={"100%"}
                        bgColor={colorMode === "light" ? "backgroundDarkGrey" : "backgroundGrey"}
                        marginTop={{ base: "170px", lg: "100px" }}
                    >
                        <Spinner
                            color={colorMode === "light" ? "AbleBlue" : "AbleYellow"}
                            marginTop={"10px"}
                            marginBottom={"100px"}
                            size={"xl"}
                            display={(nextPageNo < maxPages) && nextPageNo !== 1 ? "flex" : "none"}
                        />
                    </Flex>
                    :
                    <>
                        {
                            rows.length === 0 ?
                                <Text textAlign={"center"} py={30}>No Bundles Found!</Text>
                                :
                                <>
                                    <TableHeader>
                                        <Table.Row>
                                            <Table.ColumnHeader>Role</Table.ColumnHeader>
                                            <Table.ColumnHeader textAlign={"right"}>Function</Table.ColumnHeader>
                                        </Table.Row>
                                    </TableHeader>

                                    <Table.Body>
                                        {rows.map((role: IRole) =>
                                            <Table.Row key={role.id}>
                                                <Table.Cell width={{ base: 200, lg: 400 }}>
                                                    <HStack gap={4}>
                                                        {role.name}{isRoleActive(role) ? <FaCheckCircle size={18} /> : <></>}
                                                    </HStack>
                                                </Table.Cell>
                                                <Table.Cell ml={{ base: 0, lg: 250 }} mr={{ base: 0, lg: 100 }} textAlign={"right"}>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleToggleRole(role.id, isRoleActive(role))}
                                                        overrideBgColor={isRoleActive(role)}
                                                        bg={"red"}
                                                        disabled={pendingRoleIds[role.id] === true}
                                                        aria-busy={pendingRoleIds[role.id] === true}
                                                        loading={pendingRoleIds[role.id]}
                                                    >
                                                        {isRoleActive(role) ? <FaMinus /> : <FaPlus />}
                                                    </Button>
                                                </Table.Cell>
                                            </Table.Row>
                                        )}
                                    </Table.Body>
                                </>
                        }
                    </>
                }
            </Table.Root>
            <Box pt={2} width={"30%"} textAlign={"center"} m={"auto"} >Page {page + 1} of {maxPages}</Box>
        </>

    )
}